import axios, { AxiosResponse } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  User,
  UserCreateRequest,
  UserEditRequest,
  Keyword,
  KeywordCreateRequest,
  BatchKeywordCreateRequest,
  BatchKeywordCreateResponse,
  SearchResultsResponse,
  SortField,
  SortOrder,
} from '../types';

// 根据环境配置API基础URL
const getBaseURL = () => {
  // 开发环境：指向服务器后端端口
  if (process.env.NODE_ENV === 'development') {
    return 'http://127.0.0.1:8000/api';
  }
  // 生产环境：使用相对路径
  return '/api';
};

// 创建axios实例
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
});

// 请求拦截器：添加认证token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器：处理错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 清除过期token并重定向到登录页
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authApi = {
  login: (data: LoginRequest): Promise<AxiosResponse<LoginResponse>> =>
    api.post('/auth/login', data),
  
  getCurrentUser: (): Promise<AxiosResponse<User>> =>
    api.get('/auth/me'),
  
  getUsers: (): Promise<AxiosResponse<User[]>> =>
    api.get('/auth/users'),
  
  createUser: (data: UserCreateRequest): Promise<AxiosResponse<User>> =>
    api.post('/auth/register', data),
  
  updateUser: (id: string, data: UserEditRequest): Promise<AxiosResponse<User>> =>
    api.put(`/auth/users/${id}`, data),
  
  deleteUser: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/auth/users/${id}`),
  
  getCategories: (): Promise<AxiosResponse<string[]>> =>
    api.get('/keywords/categories/list/'),
  
  logout: () => {
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  },
};

// 关键词管理API
export const keywordApi = {
  getKeywords: (params?: {
    primary_category?: string;
    status?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<AxiosResponse<Keyword[]>> =>
    api.get('/keywords/', { params }),
  
  createKeyword: (data: KeywordCreateRequest): Promise<AxiosResponse<Keyword>> =>
    api.post('/keywords/', data),
  
  createBatchKeywords: (data: BatchKeywordCreateRequest): Promise<AxiosResponse<BatchKeywordCreateResponse>> =>
    api.post('/keywords/batch/', data),
  
  updateKeyword: (id: string, data: Partial<KeywordCreateRequest & { status: string }>): Promise<AxiosResponse<Keyword>> =>
    api.put(`/keywords/${id}/`, data),
  
  deleteKeyword: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/keywords/${id}/`),
  
  getCategories: (): Promise<AxiosResponse<string[]>> =>
    api.get('/keywords/categories/list/'),
};

// 内容查询API
export const contentApi = {
  getSearchResults: (params: {
    page?: number;
    page_size?: number;
    sort_by?: SortField;
    sort_order?: SortOrder;
  }): Promise<AxiosResponse<SearchResultsResponse>> =>
    api.get('/contents/search-results', { params }),
};

export default api; 